/**
 * 4.6 §4.3 Campus 미션(Field 판정) — FS-04 화면 규칙(순수 · 시험 대상).
 *  - 카드(FS-04-01): 아직 확인되지 않은 첫 목표의 프리셋을 미리 고른다(모두 확인됐으면 첫 목표).
 *  - HUD(FS-04-02): 지금 프리셋에 확인되지 않은 시간 목표가 있으면 가장 짧은 목표 시간(본인만 — 남의 시간 · 순위 없음).
 *  - 결과 띠(FS-04-03): 완주한 판만. 미완주는 띠 없음(DR-01 — 실패 낙인 없음 · 힌트는 기존 SR-ACH-04).
 */
import type {
  AttemptVerification,
  Mission,
  MissionGoal,
  VerificationStatus,
} from "../../../lib/api/missionApi";
import type { MissionBandState, MissionRunView, SaveResult } from "../types";

/** 이 모드 · 프리셋의 목표 */
export const goalsOn = (m: Mission | null, modeId: string, presetKey: string): MissionGoal[] =>
  (m?.goals ?? []).filter((g) => g.modeId === modeId && g.presetKey === presetKey);

/** 미리 고를 프리셋(이 모드 목표가 없으면 null) */
export function missionPreset(m: Mission | null, modeId: string): string | null {
  const mine = (m?.goals ?? []).filter((g) => g.modeId === modeId);
  return (mine.find((g) => g.state !== "verified") ?? mine[0])?.presetKey ?? null;
}

/** 남은(확인되지 않은) 목표가 있는가 — 홈 '미션 이어 하기' */
export const missionOpen = (m: Mission | null): boolean =>
  !!m && m.goals.some((g) => g.state !== "verified");

/** HUD 목표 시간(ms) — 이 프리셋의 확인되지 않은 시간 목표 중 가장 짧은 것 */
export function hudGoalMs(m: Mission | null, modeId: string, presetKey: string): number | null {
  const ts = goalsOn(m, modeId, presetKey)
    .filter((g) => g.state !== "verified" && g.timeUnderMs != null)
    .map((g) => g.timeUnderMs as number);
  return ts.length ? Math.min(...ts) : null;
}

/** 이 판이 채우는 목표(완주 · 시간 안) */
const meets = (g: MissionGoal, timeMs: number) =>
  g.timeUnderMs == null || Math.round(timeMs) <= g.timeUnderMs;

/**
 * 판 끝(저장 전) 띠. 미션 목표가 이 프리셋에 없으면 null(띠 없음).
 * 완주 · 채운 목표 중 확인되지 않은 것이 있으면 saving(저장 뒤 pending) · 모두 이미 확인 → already · 시간 밖 → over.
 */
export function bandAtFinish(
  m: Mission | null,
  modeId: string,
  presetKey: string,
  completed: boolean,
  timeMs: number,
): MissionRunView | null {
  const goals = goalsOn(m, modeId, presetKey);
  if (!goals.length || !completed) return null;
  const hit = goals.filter((g) => meets(g, timeMs));
  const open = hit.filter((g) => g.state !== "verified");
  if (open.length) return { state: "saving", goals: open };
  if (hit.length) return { state: "already", goals: hit };
  const timed = goals.filter((g) => g.timeUnderMs != null && g.state !== "verified");
  if (!timed.length) return { state: "already", goals };
  const goalMs = Math.max(...timed.map((g) => g.timeUnderMs as number));
  return { state: "over", goals: timed, goalMs, timeMs };
}

/** 저장 결과 → 띠(saving 일 때만 바뀐다) */
export function bandAfterSave(v: MissionRunView, saved: SaveResult): MissionRunView {
  if (v.state !== "saving") return v;
  if (saved.state === "offline" || saved.state === "failed" || saved.state === "tooBig")
    return { ...v, state: "unsaved" };
  if (saved.state === "none") return { ...v, state: "unsaved" };
  if (saved.verification && saved.attemptId)
    return { ...v, state: "pending", status: "pending", attemptId: saved.attemptId };
  // 저장됐지만 서버가 미션 판으로 받지 않았다(묶음 만료 등) — 띠를 거둔다
  return { ...v, state: "none" };
}

const BY_STATUS: Record<VerificationStatus, MissionBandState | null> = {
  none: "none",
  pending: null,
  verified: "verified",
  mismatch: "failed",
  failed: "failed",
};

/** 검증 조회 → 띠(pending 이면 그대로) */
export function bandAfterPoll(v: MissionRunView, r: AttemptVerification): MissionRunView {
  const next = BY_STATUS[r.status];
  return next ? { ...v, state: next, status: r.status, slow: false } : { ...v, status: r.status };
}

/** 폴링 간격 · 상한(≈2분 — 넘으면 '확인 중' 그대로 + 안내) */
export const POLL_MS = 3000;
export const POLL_MAX = 40;
